import React, { useState } from "react";
import clsx from "clsx";
import LandingGlyph, { type LandingGlyphName } from "./LandingGlyph";
import Button from "../ui/Button/Button";
import Modal from "../ui/Modal/Modal";

export type LandingCategory = {
  name: string;
  blurb: string;
  glyph: LandingGlyphName;
};

const categoryTones = [
  "border-[#14B8A6]/30 bg-[#ECFDF5] text-[#0F766E]",
  "border-[#F97316]/30 bg-[#FFF7ED] text-[#C2410C]",
  "border-myteengig-primary/25 bg-[#EAF8FF] text-myteengig-primary",
  "border-[#EAB308]/35 bg-[#FEFCE8] text-[#A16207]",
];

export type LandingCategoryShowcaseProps = React.ComponentPropsWithoutRef<"section"> & {
  categories: LandingCategory[];
  eyebrow: string;
  title: string;
  description: string;
  footnote?: string;
};

const LandingCategoryShowcase: React.FC<LandingCategoryShowcaseProps> = ({
  categories,
  eyebrow,
  title,
  description,
  footnote,
  className,
  ...props
}) => {
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const mobilePreviewCategories = categories.slice(0, 3);

  const renderCategoryCard = (category: LandingCategory, index: number, className?: string) => (
    <article
      key={category.name}
      className={clsx(
        "group rounded-xl border border-myteengig-border bg-white p-4 shadow-xs transition duration-200 hover:-translate-y-1 hover:border-myteengig-primary/40 hover:shadow-[0_18px_40px_rgba(10,70,102,0.12)]",
        className,
      )}
    >
      <span
        className={clsx(
          "flex h-12 w-12 items-center justify-center rounded-lg border transition group-hover:scale-105",
          categoryTones[index % categoryTones.length],
        )}
      >
        <LandingGlyph name={category.glyph} className="h-7 w-7" />
      </span>
      <h3 className="mt-3 text-base font-bold text-myteengig-text-primary">{category.name}</h3>
      <p className="mt-1 text-sm leading-6 text-myteengig-text-muted">{category.blurb}</p>
    </article>
  );

  return (
    <section
      className={clsx("bg-[linear-gradient(180deg,#FFFFFF_0%,#F6FCFF_100%)] px-4 py-10 sm:px-6 sm:py-16 lg:px-10", className)}
      {...props}
    >
      <div className="mx-auto max-w-7xl">
        <p className="inline-flex items-center gap-2 rounded-md border border-myteengig-border bg-white px-3 py-1.5 text-xs font-bold tracking-wide text-myteengig-primary uppercase shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-[#14B8A6]" />
          {eyebrow}
        </p>
        <h2 className="mt-3 text-3xl font-bold text-myteengig-text-primary">{title}</h2>
        <p className="mt-3 max-w-3xl text-base leading-7 text-myteengig-text-muted">{description}</p>

        <div className="mt-6 sm:hidden">
          <div className="grid gap-2">
            {mobilePreviewCategories.map((category, index) => (
              <div key={`preview-${category.name}`} className="flex items-center gap-3 rounded-lg border border-myteengig-border bg-white px-3 py-2 shadow-xs">
                <span className={clsx("h-3 w-3 shrink-0 rounded-full border", categoryTones[index % categoryTones.length])} />
                <span className="min-w-0 text-sm font-bold text-myteengig-text-primary">{category.name}</span>
              </div>
            ))}
          </div>
          <Button variant="primary" className="mt-3 w-full justify-center" onClick={() => setIsCategoryModalOpen(true)}>
            View categories
          </Button>
        </div>

        <div className="mt-8 hidden gap-3 sm:grid sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((category, index) => renderCategoryCard(category, index))}
        </div>

        {footnote ? <p className="mt-6 hidden text-sm text-myteengig-text-muted sm:block">{footnote}</p> : null}
      </div>

      <Modal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        title="Gig Categories"
        panelClassName="max-w-4xl"
      >
        <div className="grid gap-3 text-left sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category, index) => renderCategoryCard(category, index, "hover:translate-y-0 hover:shadow-sm"))}
        </div>
      </Modal>
    </section>
  );
};

export default LandingCategoryShowcase;
